import {
  Box,
  Header,
  SpaceBetween,
  TableProps,
} from "@cloudscape-design/components-themed";
import CSDTable from "@cloudscape-design/components-themed/table";
import { MouseEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import {
  useGetApplicationFileDownloadQuery,
  useGetResultFileDownloadQuery,
} from "../../features/api";
import {
  CreditCardExaminationApplicationFileDownloadRequest,
  CreditCardExaminationSearchResponse,
} from "../../features/api-interfaces";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import { formatDate } from "../../utils/format";
import Pagination from "../UI/Pagination";
import Preference from "../UI/Preference";
import { COMMON_LABELS } from "../default-constants";
import { LABELS, ORDER_TARGET_CODE_OMNI } from "./constants";

interface ICreditCardExaminationTableItems {
  applicationFileNo: number;
  orderTargetCode: number;
  orderTargetName: string;
  applicationStartDate: string;
  applicationFileName: string;
  applicationFileNameStorageDestination: string;
  applicationResultFileName: string;
  applicationResultFileNameStorageDestination: string;
  applicationResultResponseDate: string;
  responseDate: string;
  resultFileName: string;
  resultFileStorageDestination: string;
  applicationStatusName: string;
}

interface ITable {
  data: CreditCardExaminationSearchResponse[] | undefined;
  isFetching: boolean;
  refetch: Function;
}

const Table = (props: ITable) => {
  const { data, isFetching, refetch } = props;

  /**
   * 申請ファイル名表示.
   */
  const applicationFileNameCell = useCallback(
    (item: ICreditCardExaminationTableItems) => (
      <Link
        to={""}
        onClick={(event) =>
          applicationFileDownload(
            event,
            item.applicationFileNo,
            item.applicationFileName,
            item.applicationFileNameStorageDestination,
            item.orderTargetName
          )
        }
      >
        {item.applicationFileName}
      </Link>
    ),
    []
  );

  /**
   * 申込結果ファイル名表示.
   */
  const ApplicationResultFileNameCell = useCallback(
    (item: ICreditCardExaminationTableItems) =>
      item.orderTargetCode === ORDER_TARGET_CODE_OMNI.OMNI_VM ? (
        <Link
          to={""}
          onClick={(event) =>
            resultFileDownload(
              event,
              item.applicationResultFileName,
              item.applicationResultFileNameStorageDestination
            )
          }
        >
          {item.applicationResultFileName}
        </Link>
      ) : (
        COMMON_LABELS.HYPHEN
      ),
    []
  );

  /**
   * 申請結果ファイル名表示.
   */
  const resultFileNameCell = useCallback(
    (item: ICreditCardExaminationTableItems) => (
      <Link
        to={""}
        onClick={(event) =>
          resultFileDownload(
            event,
            item.resultFileName,
            item.resultFileStorageDestination
          )
        }
      >
        {item.resultFileName}
      </Link>
    ),
    []
  );

  const columnDefinitions: TableProps.ColumnDefinition<ICreditCardExaminationTableItems>[] =
    [
      {
        id: "orderTargetName",
        header: LABELS.ORDER_TARGET_NAME,
        cell: (item: ICreditCardExaminationTableItems) => item.orderTargetName,
      },
      {
        id: "applicationStartDate",
        header: LABELS.FILING_DATE,
        cell: (item: ICreditCardExaminationTableItems) =>
          item.applicationStartDate,
      },
      {
        id: "applicationFileName",
        header: LABELS.APPLICATION_FILE_NAME,
        cell: applicationFileNameCell,
      },
      {
        id: "applicationResultResponseDate",
        header: LABELS.APPLICATION_RESULT_RESPONSE_DATE,
        cell: (item: ICreditCardExaminationTableItems) =>
          item.orderTargetCode === ORDER_TARGET_CODE_OMNI.OMNI_VM
            ? item.applicationResultResponseDate
            : COMMON_LABELS.HYPHEN,
      },
      {
        id: "applicationResultFileName",
        header: LABELS.APPLICATION_RESULT_FILE_NAME,
        cell: ApplicationResultFileNameCell,
      },
      {
        id: "responseDate",
        header: LABELS.RESPONSE_DATE,
        cell: (item: ICreditCardExaminationTableItems) =>
          formatDate(item.responseDate),
      },
      {
        id: "resultFileName",
        header: LABELS.RESULT_FILE_NAME,
        cell: resultFileNameCell,
      },
      {
        id: "applicationStatusName",
        header: LABELS.APPLICATION_STATUS,
        cell: (item: ICreditCardExaminationTableItems) =>
          item.applicationStatusName,
      },
    ];

  const getApplicationStatus = (applicationStatus: string): string => {
    switch (applicationStatus) {
      case "1":
        return LABELS.APPLICATION_STATUS_APPLYING;
      case "2":
        return LABELS.APPLICATION_STATUS_PART_COMPLETE;
      case "3":
        return LABELS.APPLICATION_STATUS_COMPLETE;
      case "4":
        return LABELS.APPLICATION_STATUS_DOWNLOAD_WAIT;
      default:
        // 想定外の数値の場合は空文字を設定する。
        return "";
    }
  };

  // 一覧データの加工
  const listData = data
    ? data.map((item) => ({
        ...item,
        filingDate: item.applicationStartDate,
        responseDate: item.applicationResponseDate,
        applicationStatusName: getApplicationStatus(item.applicationStatus),
      }))
    : [];

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id!,
    label: column.header as string,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });
  const [pagedItems, setPagedItems] = useState<
    ICreditCardExaminationTableItems[]
  >([]);
  const [allItems, setAllItems] = useState<ICreditCardExaminationTableItems[]>(
    []
  );

  useEffect(() => {
    if (
      data !== undefined ||
      data !== ({} as CreditCardExaminationSearchResponse[])
    ) {
      setPagedItems(
        getPagedItems(
          listData,
          1,
          preferences.pageSize
        ) as ICreditCardExaminationTableItems[]
      );
      setAllItems(listData);
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (data !== undefined) {
      setPagedItems(listData.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  // ダウンロード
  const [applicationFileDownloadAction, setApplicationFileDownloadAction] =
    useState(false);
  const [resultFileDownloadAction, setResultFileDownloadAction] =
    useState(false);
  const [applicationFileName, setApplicationFileName] =
    useState<CreditCardExaminationApplicationFileDownloadRequest>({
      applicationFileNo: 0,
      orderTargetName: "",
      fileName: "",
      applicationFileNameStorageDestination: "",
    });
  const [resultFileName, setResultFileName] =
    useState<CreditCardExaminationApplicationFileDownloadRequest>({
      applicationFileNo: 0,
      orderTargetName: "",
      fileName: "",
      applicationFileNameStorageDestination: "",
    });
  useGetApplicationFileDownloadQuery(applicationFileName, {
    skip: !applicationFileDownloadAction,
    refetchOnMountOrArgChange: true,
  });
  useGetResultFileDownloadQuery(resultFileName, {
    skip: !resultFileDownloadAction,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setApplicationFileDownloadAction(false);
  }, [applicationFileDownloadAction, refetch]);

  useEffect(() => {
    setResultFileDownloadAction(false);
  }, [resultFileDownloadAction, refetch]);

  /**
   * 申請ファイル名リンク押下時の処理.
   * @param {MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>} event
   * @param {string} fileName 申請ファイル名
   */
  const applicationFileDownload = (
    event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>,
    applicationFileNo: number,
    fileName: string,
    applicationFileNameStorageDestination: string,
    orderTargetName: string
  ) => {
    event.preventDefault();
    const applicationFileDownloadData = {
      applicationFileNo: applicationFileNo,
      orderTargetName: orderTargetName,
      fileName: fileName,
      applicationFileNameStorageDestination:
        applicationFileNameStorageDestination,
    } as CreditCardExaminationApplicationFileDownloadRequest;
    setApplicationFileDownloadAction(true);
    setApplicationFileName(applicationFileDownloadData);
  };

  /**
   * 結果ファイル名リンク押下時の処理.
   * @param {MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>} event
   * @param fileName 結果ファイル名
   */
  const resultFileDownload = (
    event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>,
    fileName: string,
    resultFileStorageDestination: string
  ) => {
    event.preventDefault();
    const resultFileDownloadData = {
      orderTargetName: "",
      fileName: fileName,
      applicationFileNameStorageDestination: resultFileStorageDestination,
    } as CreditCardExaminationApplicationFileDownloadRequest;
    setResultFileDownloadAction(true);
    setResultFileName(resultFileDownloadData);
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <CSDTable
        columnDefinitions={columnDefinitions}
        loading={isFetching}
        loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
        items={pagedItems}
        resizableColumns
        stickyHeader
        header={
          <SpaceBetween size="xs">
            <SpaceBetween direction="horizontal" size="m"></SpaceBetween>
            <Header>
              {LABELS.ANSWER_STATUS_LABEL}
              {getHeaderCounterText(allItems.length, pagedItems.length)}
            </Header>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            filteredItems={allItems}
            setCurrentPageIndex={setCurrentPageIndex}
            pageSize={preferences.pageSize}
            setPagedItems={setPagedItems}
          />
        }
        preferences={
          <Preference
            preferences={preferences}
            options={options}
            handleOnConfirm={handleOnConfirm}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            {COMMON_LABELS.CSD.NO_DATA}
          </Box>
        }
      />
    </SpaceBetween>
  );
};

export default Table;
